import {
  faEye,
  faEyeSlash,
  faKey,
  faLock,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppContext } from 'providers/AppProvider';
import Button from 'components/base/Button';
import { useEffect, useState } from 'react';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';

export interface UserAccess {
  title: string;
  level: number;
}

export interface UserInfo {
  name: string;
  email: string;
  password: string;
  access: UserAccess;
}

export interface RememberMeData {
  remember: boolean;
  expires: number;
}

// const SignInForm = ({ layout }: { layout: 'simple' | 'card' | 'split' }) => {
const SignInForm = () => {
  const navigate = useNavigate();
  const [validated, setValidated] = useState(false);
  const [credentialsValidity, setCredentialsValidity] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    pin: 0,
    rememberMe: false
  });
  const [isPinMode, setIsPinMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { getCurrentCompany, login, loginWithPin } = useAppContext();
  const company = getCurrentCompany();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');

  const checkRememberMe = () => {
    const rememberMeData = localStorage.getItem('rememberMe');
    if (rememberMeData) {
      try {
        const parsed: RememberMeData = JSON.parse(rememberMeData);
        const now = Date.now();

        if (parsed.remember && parsed.expires > now) {
          setIsPinMode(true);
        } else {
          localStorage.removeItem('rememberMe');
        }
      } catch (error) {
        localStorage.removeItem('rememberMe');
      }
    } else {
      setFormData({
        email: '',
        password: '',
        pin: 0,
        rememberMe: false
      });
      setIsPinMode(false);
    }
  };

  useEffect(() => {
    checkRememberMe();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (credentialsValidity === false) {
      setCredentialsValidity(true);
    }

    const { id, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : value
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.stopPropagation();
      setIsLoading(false);
      setValidated(true);
      return;
    }

    setIsLoading(true);
  };

  useEffect(() => {
    const processLogin = async () => {
      if (isLoading && company) {
        try {
          await login(
            formData.email,
            formData.password,
            company?.id_entidade || 0
          )
            .then(() => {
              if (formData.rememberMe) {
                const expirationTime = Date.now() + 10 * 24 * 60 * 60 * 1000; // Set expiration for 10 hours from now

                const loginPermance = {
                  remember: true,
                  expires: expirationTime
                };

                localStorage.setItem(
                  'rememberMe',
                  JSON.stringify(loginPermance)
                );
              }
              setIsLoading(false);
              navigate(redirect || '/dashboard');
            })
            .catch(() => {
              setIsLoading(false);
              setCredentialsValidity(false);
            });
        } catch (error) {
          setIsLoading(false);
          setCredentialsValidity(false);
        }
      }
    };

    processLogin();
  }, [isLoading, company, formData]);

  const handlePinLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);

    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.stopPropagation();

      setIsLoading(false);
      setValidated(true);
      return;
    }

    try {
      await loginWithPin(formData.pin, company?.id_entidade || 0)
        .then(() => {
          navigate(redirect || '/dashboard');
        })
        .catch(() => {
          setIsLoading(false);
          setCredentialsValidity(false);
          checkRememberMe();
        });
    } catch (error) {
      setIsLoading(false);
      setCredentialsValidity(false);
    }
  };

  const switchToFullLogin = () => {
    localStorage.removeItem('rememberMe');
    setFormData({
      email: '',
      password: '',
      pin: 0,
      rememberMe: false
    });
    setIsPinMode(false);
  };

  // PIN Login Form
  if (isPinMode) {
    return (
      <Form noValidate validated={validated} onSubmit={handlePinLogin}>
        {/* <div className="text-center mb-7">
          <h3 className="text-body-highlight">Welcome Back</h3>
        </div> */}
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="pin">PIN</Form.Label>
          <div className="form-icon-container">
            <Form.Control
              required
              id="pin"
              type="password"
              className="form-icon-input text-center"
              placeholder="••••"
              maxLength={4}
              minLength={4}
              onChange={handleInputChange}
              isInvalid={!credentialsValidity}
              style={{ letterSpacing: '0.5em' }}
            />
            <FontAwesomeIcon
              icon={faLock}
              className="text-body fs-9 form-icon"
            />
          </div>
        </Form.Group>
        <Button type="submit" variant="primary" className="w-100 mb-3">
          {isLoading ? (
            <Spinner
              animation="border"
              role="status"
              variant="light"
              size="sm"
            />
          ) : (
            'Iniciar sessão'
          )}
        </Button>
        <div className="text-center">
          <Button
            variant="link"
            onClick={switchToFullLogin}
            className="p-0 fs-9"
          >
            Utilizar email e palavra-passe
          </Button>
        </div>
      </Form>
    );
  }

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      {/* <div className="text-center mb-7">
        <h3 className="text-body-highlight">Sign In</h3>
      </div> */}
      <Form.Group className="mb-3 text-start">
        <Form.Label htmlFor="email">Email</Form.Label>
        <div className="form-icon-container">
          <Form.Control
            required
            id="email"
            type="email"
            className="form-icon-input"
            placeholder="nome@empresa.com"
            value={formData.email}
            onChange={handleInputChange}
            isInvalid={!credentialsValidity}
            autoComplete="none"
          />
          <FontAwesomeIcon icon={faUser} className="text-body fs-9 form-icon" />
        </div>
      </Form.Group>
      <Form.Group className="mb-3 text-start">
        <Form.Label htmlFor="password">Palavra-passe</Form.Label>
        <div className="form-icon-container">
          <Form.Control
            required
            id="password"
            type={showPassword ? 'text' : 'password'}
            className="form-icon-input"
            placeholder="Palavra-passe"
            value={formData.password}
            onChange={handleInputChange}
            isInvalid={!credentialsValidity}
          />
          <button
            type="button"
            aria-label={
              showPassword ? 'Ocultar palavra-passe' : 'Mostrar palavra-passe'
            }
            onClick={togglePasswordVisibility}
            className="btn btn-link p-0 border-0"
            style={{
              position: 'absolute',
              top: '50%',
              right: '0.75rem',
              transform: 'translateY(-50%)',
              zIndex: 3,
              color: 'var(--bs-body-color)'
            }}
          >
            <FontAwesomeIcon
              icon={showPassword ? faEyeSlash : faEye}
              className="text-body fs-9"
            />
          </button>
          <FontAwesomeIcon icon={faKey} className="text-body fs-9 form-icon" />
        </div>
      </Form.Group>
      <Row className="flex-between-center mb-7">
        <Col xs="auto">
          <Link
            to={`https://www.ctcp.pt/users/forgotepassword.aspx`}
            // to={`/pages/authentication/${layout}/forgot-password`}
            className="fs-9 fw-semibold"
          >
            Esqueceu-se da palavra-passe?
          </Link>
        </Col>
      </Row>
      <Button type="submit" variant="primary" className="w-100 mb-3">
        {isLoading ? (
          <Spinner animation="border" role="status" variant="light" size="sm" />
        ) : (
          'Iniciar sessão'
        )}
      </Button>
    </Form>
  );
};

export default SignInForm;
